import React, { useContext } from 'react';
import * as styles from './related-products.module.css';
import CtaLink from '../Global/CtaLink';
import CtaButton from '../Global/CtaButton';
import { ProductPagePreview } from '../Global/DataUtils/productQueries';
import { ProductDescription } from '../../templates/StructuredDataContext';
import { getMultiChoice } from '../Global/DataUtils';
import RichText from '../Global/RichText';
import {
    optimizeKontentImageUrl,
    buildKontentSrcSet,
} from '../Global/DataUtils';
import { PageMetadataContext } from '../../templates/PageMetaDataContext';
import { getLangPrefix } from '../../global/countries';

interface Props {
    product: ProductPagePreview;
    productCtaLabel?: string;
    truncateLines?: number;
    productStyle?: string;
    ctaStyle?: string;
    backgroundColor?: string;
    fontColor?: string;
    customCtaLink?: string;
    useCustomCtaLocation?: boolean;
}

const ProductTile: React.FC<Props> = ({
    productCtaLabel,
    customCtaLink,
    useCustomCtaLocation,
    product,
    truncateLines,
    productStyle,
    ctaStyle,
    backgroundColor,
    fontColor,
}) => {
    const pageMetadata = useContext(PageMetadataContext);
    const langPrefix = getLangPrefix(pageMetadata?.preferredLanguage);
    const authorStyles =
        backgroundColor || fontColor
            ? {
                  backgroundColor: backgroundColor,
                  color: fontColor,
              }
            : undefined;

    const ctaColor = fontColor || '#505251';

    const productDescription = product.elements.page_components.value.filter(
        (component) =>
            component.__typename ===
            'kontent_item_component___product_description'
    )[0] as ProductDescription;

    const productElements = productDescription.elements;

    const productImageUsage = getMultiChoice(
        productElements.related_product_image_usage
    );

    const productImage =
        productImageUsage === 'use_image_gallery_image'
            ? productElements?.description_image?.value[0]?.elements.file
                  ?.value[0]
            : productElements?.related_product_image?.value[0]?.elements.file
                  ?.value[0];

    return (
        <div className={productStyle} style={authorStyles}>
            {productImage && (
                <CtaButton
                    href={
                        useCustomCtaLocation && customCtaLink
                            ? customCtaLink
                            : product.elements?.slug.url
                            ? `${langPrefix}${product.elements?.slug.url}`
                            : 'undefined'
                    }
                    target="_self"
                >
                    <div data-kontent-element-codename="description_image">
                        <img
                            src={optimizeKontentImageUrl(
                                productImage.url as string,
                                { w: 760, fit: 'crop', q: 80, auto: 'format' }
                            )}
                            srcSet={buildKontentSrcSet(
                                productImage.url as string,
                                760,
                                { fit: 'crop', q: 80, auto: 'format' }
                            )}
                            sizes="(max-width: 760px) 100vw, 760px"
                            alt={
                                productImage?.description ||
                                productImage?.name ||
                                ''
                            }
                            width={760}
                            height={520}
                            loading="lazy"
                            style={{
                                width: '100%',
                                height: 'auto',
                                objectFit: 'cover',
                            }}
                        />
                    </div>
                </CtaButton>
            )}
            <div className={styles.productDetails}>
                {productElements.product_display_name && (
                    <h3 data-kontent-element-codename="product_display_name">
                        {productElements.product_display_name?.value}
                    </h3>
                )}
                <div className={styles.description}>
                    {productElements.sub_title?.value && (
                        <div
                            className={styles.productSubtitle}
                            data-kontent-element-codename="sub_title"
                        >
                            {productElements.sub_title.value}
                        </div>
                    )}
                    {productElements?.short_description &&
                        RichText(
                            productElements?.short_description,
                            undefined,
                            undefined,
                            undefined,
                            'short_description'
                        )}
                </div>
                <div className={ctaStyle}>
                    <CtaLink
                        href={
                            useCustomCtaLocation && customCtaLink
                                ? customCtaLink
                                : `${langPrefix}${product.elements?.slug.url}`
                        }
                        target="_self"
                        color={ctaColor}
                    >
                        {productCtaLabel}
                    </CtaLink>
                </div>
            </div>
        </div>
    );
};

export default ProductTile;
